// Sugalarm — keep-screen-awake bridge wrapper (cycle 282).
//
// The single owner of access to the native `KeepAwake` plugin. That plugin
// exists ONLY in the Android kiosk APK (the `kiosk` product flavor); it is
// absent on web, the Pi kiosk, iPhone, the phone layout, and the `standard`
// Android flavor — so `isAvailable()` is false on every surface except the
// kiosk APK, and the Settings "Keep screen on" card gates strictly on it.
//
// Like every native plugin in the pure-remote webview (cycle 26/138), it is
// reached through the bridge-injected `window.Capacitor.Plugins` proxy —
// NEVER a bare `import("@capacitor/...")`, which has no bundler to resolve
// against at runtime. Mirrors `lib/screenBrightness.ts`.
//
// The choice is TABLET-LOCAL: it lives in the tablet's SharedPreferences via
// the native plugin, and is never sent to the server (no `/api/settings` key
// exists for it by construction), the phone, or the Pi. When ON the plugin
// applies device-owner never-sleep settings (OS screen-off timeout to "never"
// + stay-on-while-plugged) on top of FLAG_KEEP_SCREEN_ON.

import { isCapacitorNative } from "./capacitor";

export interface KeepAwakeConfig {
  /** When true, the tablet screen is pinned on and never sleeps. */
  keepAwake: boolean;
}

interface KeepAwakeProxy {
  getConfig(): Promise<KeepAwakeConfig>;
  setConfig(cfg: KeepAwakeConfig): Promise<KeepAwakeConfig>;
}

/** Resolve the natively-registered plugin proxy off the Capacitor bridge,
 *  or null on any surface where it isn't present. */
function plugin(): KeepAwakeProxy | null {
  if (!isCapacitorNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.KeepAwake;
  return p != null ? (p as KeepAwakeProxy) : null;
}

/** True iff the native KeepAwake plugin is registered on the bridge — i.e. the
 *  SPA is running inside the Android kiosk APK. The Settings card renders only
 *  when this is true. */
export function isAvailable(): boolean {
  return plugin() != null;
}

/** Read the tablet-local keep-awake config, or null on a non-kiosk-APK
 *  surface / on any bridge error. */
export async function getConfig(): Promise<KeepAwakeConfig | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    return await p.getConfig();
  } catch (err) {
    console.warn("[keepAwake] getConfig failed", err);
    return null;
  }
}

/** Write the tablet-local keep-awake choice. No-ops on a non-kiosk-APK
 *  surface. Tablet-local — NOT a `/api/settings` PATCH, no WS broadcast. */
export async function setConfig(cfg: KeepAwakeConfig): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.setConfig(cfg);
  } catch (err) {
    console.warn("[keepAwake] setConfig failed", err);
  }
}
